import React, { useState, useEffect } from 'react';
import { ucFirst } from '../../common/ambient';
import classNames from 'classnames';
import Avatar from '../user/Avatar';
import { alertSetOk } from '.'
import bindAllActions from '../../common/bindAllActions'
import { path } from 'ramda'
import {
  isValidPhone,
  isEmailVerified,
  isPhoneVerified,
  hasVerifiedContact as hasVerifiedContactHelper,
  getEmailInviteStatus,
  getPhoneInviteStatus,
  createInviteAction,
  shouldShowEmailField,
  shouldShowPhoneField,
  hasValidContact as hasValidContactHelper,
  validateForm as validateFormHelper
} from './AlertSetMemberCard.helpers'

const AlertSetMemberCard = ({ member, editing, alertSets, onChange, onCancel, socialActions, user, ...props }) => {
  alertSets = alertSets || []
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    alertPrefs: [],
    alertSets: [],
    admin: false
  });
  const [alertSetState, setAlertSetState] = useState({
    _id: '',
    alertPrefs: []
  })

  const [validationError, setValidationError] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [sendingEmailInvite, setSendingEmailInvite] = useState(false);

  // Helper function to send email verification
  const sendEmailVerification = (memberData, onSuccess, onError) => {
    setSendingEmailInvite(true);

    const action = createInviteAction(
      'alert-set-group-member-email',
      member._id,
      memberData.email || formState.email,
      memberData.name || formState.name
    );

    socialActions.addUserAction(action)
    .then(res => {
      setSendingEmailInvite(false);
      if (res && res._id && onSuccess) {
        onSuccess(res);
      }
    })
    .catch(err => {
      setSendingEmailInvite(false);
      console.error('Failed to create user action:', err);
      if (onError) {
        onError(err);
      }
    });
  };

  // Check if current form state is different from original member data
  const hasChanges = () => {
    return formState.name !== (member.name || '') ||
           formState.email !== (member.email || '') ||
           formState.phone !== (member.phone || '') ||
           JSON.stringify(formState.alertSets) !== JSON.stringify(member.alertSets) ||
           JSON.stringify(formState.alertPrefs.sort()) !== JSON.stringify((member.alertPrefs || []).sort());
  };

  useEffect(() => {
    setFormState({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      alertPrefs: member.alertPrefs || [],
      alertSets: member.alertSets || [],
      admin: member.admin || false,
      inviteMethod: member.inviteMethod // Include the invite method in the form state
    });
    setValidationError('');
  }, [member]);

  const handleCheckboxChange = (pref, prop = 'alertPrefs') => {
    setFormState(prev => {
      const newAlertPrefs = prev[prop].includes(pref)
        ? prev[prop].filter(p => p !== pref)
        : [...prev[prop], pref];
      
      return {
        ...prev,
        [prop]: newAlertPrefs
      };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate form after each input change
    if (name === 'email' || name === 'phone') {
      const error = validateFormHelper(member, { ...formState, [name]: value });
      setValidationError(error);
    }
  };

  const handleSave = () => {
    // Validate before saving
    const error = validateFormHelper(member, formState);
    setValidationError(error);

    if (!hasValidContactHelper(member, formState)) {
      return; // Don't proceed if validation fails
    }
    
    // Create the updated member object
    const updatedMember = {
      ...member,
      ...formState
    };
    
    // Clear uaId if the phone was changed
    if (member.clearUaId) {
      updatedMember.uaId = null;
      delete updatedMember.clearUaId;
    }
    
    // For SMS invites, automatically send the SMS when saving for the first time
    if (member.inviteMethod === 'sms' && formState.phone && isValidPhone(formState.phone) && !member.uaId) {
      setSendingInvite(true);

      const action = createInviteAction(
        'alert-set-group-member-sms',
        member._id,
        formState.phone,
        formState.name
      );

      socialActions.addUserAction(action)
      .then(res => {
        setSendingInvite(false);
        // Update member with the uaId from the created userAction and save
        if (res && res._id) {
          const finalMember = {
            ...updatedMember,
            uaId: res._id,
            phoneInvitePending: true
          };
          onChange(finalMember);
        } else {
          onChange(updatedMember);
        }
      })
      .catch(err => {
        setSendingInvite(false);
        console.error('Failed to create user action:', err);
        alert('Failed to send SMS invite. The member will be saved but you may need to send the invite later.');
        onChange(updatedMember);
      });
    }
    // For Email invites, automatically send the email when saving for the first time
    else if (member.inviteMethod === 'email' && formState.email && !member.uaId) {
      sendEmailVerification(
        formState,
        (res) => {
          // Update member with the uaId from the created userAction and save
          const finalMember = {
            ...updatedMember,
            uaId: res._id,
            emailVerified: false,
            emailInvitePending: true
          };
          onChange(finalMember);
        },
        () => {
          alert('Failed to send email invite. The member will be saved but you may need to send the invite later.');
          onChange(updatedMember);
        }
      );
    }
    // When an existing member changes their email, require re-verification
    else if (member.email && formState.email !== member.email && member.emailVerified) {
      sendEmailVerification(
        formState,
        (res) => {
          // Reset email verification status and save with new uaId
          const finalMember = {
            ...updatedMember,
            uaId: res._id,
            emailVerified: false, // Reset to unverified since email changed
            emailInvitePending: true
          };
          onChange(finalMember);
        },
        () => {
          alert('Failed to send email verification. The member will be saved but you may need to send the invite later.');
          // Still save with reset verification status
          const finalMember = {
            ...updatedMember,
            emailVerified: false,
            emailInvitePending: false
          };
          onChange(finalMember);
        }
      );
    } else {
      // For all other cases, just save normally
      onChange(updatedMember);
    }
  };

  const handleCancel = () => {
    setFormState({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      alertPrefs: member.alertPrefs || [],
      alertSets: member.alertSets || [],
      admin: member.admin || false,
      inviteMethod: member.inviteMethod // Preserve the invite method
    });
    setValidationError('');
    onCancel();
  };

  const renderField = (label, value, fieldName) => {
    // If value is not set, don't render the field at all in non-editing mode
    if (!value) {
      return null;
    }

    let extraContent = null;

    // For email fields with a value
    if (fieldName === 'email' && value) {
      const status = getEmailInviteStatus(member, user);

      if (status === 'verified') {
        extraContent = <span className={`invited-status accepted`}></span>;
      } else if (status === 'pending') {
        extraContent = <span className={`invited-status`}>pending</span>;
      } else if (status === 'can_send_invite') {
        extraContent = sendingEmailInvite ? (
          <span className="sending-invite">sending...</span>
        ) : (
          <a
            className="send-invite-link"
            onClick={() => {
              sendEmailVerification(
                member,
                (res) => {
                  if (onChange) {
                    onChange({
                      ...member,
                      uaId: res._id,
                      emailVerified: false,
                      emailInvitePending: true
                    });
                  }
                },
                () => {
                  alert('Failed to send email invite. Please try again.');
                }
              );
            }}
          >
            send invite
          </a>
        );
      }
    }

    // For phone fields with a value
    if (fieldName === 'phone' && value) {
      const status = getPhoneInviteStatus(member, user);

      if (status === 'verified') {
        extraContent = <span className={`invited-status accepted`}></span>;
      } else if (status === 'pending') {
        extraContent = <span className={`invited-status`}>pending</span>;
      } else if (status === 'can_send_invite') {
        extraContent = sendingInvite ? (
          <span className="sending-invite">sending...</span>
        ) : (
          <a
            className="send-invite-link"
            onClick={() => {
              setSendingInvite(true);

              const action = createInviteAction(
                'alert-set-group-member-sms',
                member._id,
                member.phone,
                member.name
              );

              socialActions.addUserAction(action)
              .then(res => {
                setSendingInvite(false);
                // Update member with the uaId from the created userAction
                if (onChange && res && res._id) {
                  onChange({
                    ...member,
                    uaId: res._id,
                    phoneInvitePending: true
                  });
                }
              })
              .catch(err => {
                setSendingInvite(false);
                console.error('Failed to create user action:', err);
                alert('Failed to send invite. Please try again.');
              });
            }}
          >
            send invite
          </a>
        );
      }
    }

    return (
      <div className="form-line">
        <label>{label}:</label>
        <span className="value">{value}</span>
        {extraContent && <small className="action-links">&nbsp;{extraContent}</small>}
      </div>
    );
  };

  const editingAndNotAdmin = editing && !formState.admin;

  const renderAlertPrefs = () => {
    if (editing) {
      return (
        <div className="form-line alert-prefs">
          <label>Alert Preferences</label>
          <div className="checkbox-group">
            {['email', 'sms', 'app'].map(pref => (
              <label key={pref} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formState.alertPrefs.includes(pref)}
                  onChange={() => handleCheckboxChange(pref)}
                />
                {ucFirst(pref)}
              </label>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="form-line">
        <label>Alert Preferences:</label>
        <span className="value">
          {formState.alertPrefs.length > 0 
            ? formState.alertPrefs.join(', ') 
            : 'None set'}
        </span>
      </div>
    );
  };
  const renderAlertSets = () => {
    // Only show alert options for verified contact methods
    const alertOptions = []
    if (isEmailVerified(member, user)) {
      alertOptions.push('email')
    }
    if (isPhoneVerified(member, user)) {
      alertOptions.push('sms')
    }
    const remainingAlertSets = alertSets
      .filter(_as => !formState.alertSets.find(mas => mas._id === _as._id))
      .filter(_as => alertSetOk(_as))
    const prefClasses = {
      email: 'glyphicon glyphicon-envelope',
      sms: 'glyphicon glyphicon-comment',
    }
    
    // Check if the member has either verified email or verified phone
    const hasEmailOrVerifiedPhone = hasVerifiedContactHelper(member, user)

    // If in editing mode and no email/verified phone, don't render alert sets section at all
    if (editing && !hasEmailOrVerifiedPhone && !member.admin) {
      return null
    }
    
    // If not editing and no alert sets, don't show this section at all (we'll show a button in AlertSets instead)
    if (!editing && (!formState.alertSets || formState.alertSets.length === 0)) {
      return null
    }
    
    return (
      <div className="form-line render-alert-sets">
        <label>Alert Sets</label>
        <div>
          {/* Only show alert set selector in edit mode if they have email or verified phone */}
          {editing && remainingAlertSets.length > 0 && (
            <>
              <select
                className='form-control'
                value={alertSetState._id}
                onChange={e => {
                  setAlertSetState({
                    _id: e.target.value,
                    alertPrefs: e.target.value ? alertOptions : []
                  })
                }}>
                <option value="">Select Alert Set</option>
                {remainingAlertSets.map((set, i) => (
                    <option key={i} value={set._id}>{set.title}</option>
                ))}
              </select>
              <div className="form-line alert-prefs">
                <div className="checkbox-group">
                  {alertOptions.map(pref => (
                    <label key={pref} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={alertSetState.alertPrefs && alertSetState.alertPrefs.includes(pref)}
                        onChange={() => {
                          setAlertSetState({
                            ...alertSetState,
                            alertPrefs: alertSetState.alertPrefs.includes(pref)
                              ? alertSetState.alertPrefs.filter(p => p !== pref)
                              : [...alertSetState.alertPrefs, pref]
                          })
                        }}
                      />
                      <i className={prefClasses[pref]} />
                      &nbsp;
                      {ucFirst(pref)}
                    </label>
                  ))}
                </div>
              </div>
              {alertSetState._id && alertSetState.alertPrefs.length > 0 && (
                <a
                  className="add-alert-set"
                  onClick={() => {
                    setFormState({
                      ...formState,
                      alertSets: [...formState.alertSets, alertSetState]
                    })
                    setAlertSetState({
                      _id: '',
                      alertPrefs: []
                    })
                  }}
                >Add Alert Set</a>
              )}
            </>
          )}
          <div className="member-alert-sets">
            {formState.alertSets.map((as, i) => {
              const alertSet = alertSets.find(_as => _as._id === as._id)
              const title = alertSet ? alertSet.title : 'alert set removed'
              return (
                <div className={classNames('member-alert-set alert-description', { inactive: !alertSet })} key={i}>
                  <span>{title}</span>
                  <div className='tools'>
                    {alertOptions.map(pref => {
                      const isActive = as.alertPrefs.includes(pref);
                      if (editing && hasEmailOrVerifiedPhone) {
                        // In edit mode - clickable icons
                        return (
                        <a 
                            key={pref}
                            onClick={() => {
                              const newAlertSets = formState.alertSets.map(alertSet => {
                                if (alertSet._id === as._id) {
                                  return {
                                    ...alertSet,
                                    alertPrefs: isActive
                                      ? alertSet.alertPrefs.filter(p => p !== pref)
                                      : [...alertSet.alertPrefs, pref]
                                  };
                                }
                                return alertSet;
                              });
                              setFormState({
                                ...formState,
                                alertSets: newAlertSets
                              });
                            }}
                          >
                            <i className={classNames(prefClasses[pref], { active: isActive })} />
                          </a>
                        );
                      }
                      // In view mode - just display icons
                      return (
                        <i
                          key={pref}
                          className={classNames(prefClasses[pref], { active: isActive })}
                        />
                      );
                    })}
                    {editing && hasEmailOrVerifiedPhone && (
                      <a className="remove-alert-set" onClick={() => {
                        setFormState({
                          ...formState,
                          alertSets: formState.alertSets.filter(mas => mas._id !== as._id)
                        })
                      }} >
                        <i className="active glyphicon glyphicon-trash trash" />
                      </a>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classNames("group-member-card", { editing, 'not-editing': !editing })}>
      <header>
        <Avatar name={!formState.admin && (formState.name || '?')} />
        {formState.admin ? (
          <span className="member-name">Group Admin (You)</span>
        ) : (
          editingAndNotAdmin ? (
            <div className="form-line">
              <input
                type="text"
                name="name"
                className="form-control"
                value={formState.name}
                onChange={handleInputChange}
                placeholder="Enter member name"
              />
            </div>
          ) : (
            <span className="member-name">{formState.name || 'Unnamed Member'}</span>
          )
        )}
      </header>
      
      {editingAndNotAdmin ? (
        <>
          {/* Show email field based on helper logic */}
          {shouldShowEmailField(member) && (
            <div className={classNames("form-line", { error: validationError && validationError.includes('email') })}>
              <label>Email</label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={formState.email}
                onChange={handleInputChange}
                placeholder="Enter email address"
              />
            </div>
          )}

          {/* Show phone field based on helper logic */}
          {shouldShowPhoneField(member) && (
            <div className={classNames("form-line", { error: validationError && validationError.includes('phone') })}>
              <label>Phone</label>
              {(member.phoneInvitePending || (member.uaId && member.inviteMethod === 'sms')) && formState.phone ? (
                <div className="phone-invited">
                  <span className="value">{formState.phone}</span>
                  {member.phoneVerified 
                    ? <span className="invited-badge accepted"></span>
                    : <span className="invited-badge">pending</span>
                  }
                  <a 
                    className="change-phone-link"
                    onClick={() => {
                      if (window.confirm('This will remove the SMS invite. Continue?')) {
                        setFormState({
                          ...formState,
                          phone: ''
                        });
                        // When we save, we'll need to clear the uaId
                        // Create a modified member object to use when saving
                        member.clearUaId = true;
                      }
                    }}
                  >
                    change
                  </a>
                </div>
              ) : (
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  value={formState.phone}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit phone number"
                />
              )}
            </div>
          )}
          
          {validationError && (
            <div className="form-line error">
              <span className="error-message">{validationError}</span>
            </div>
          )}
        </>
      ) : (
        <>
          {renderField('Email', formState.email, 'email')}
          {renderField('Phone', formState.phone, 'phone')}
        </>
      )}

      {renderAlertSets()}

      {editing && (
        <div className="btns">
          {hasChanges() && (
            <>
              {/* Only show save button for email invite when both name and email are valid */}
              {member.inviteMethod === 'email' && formState.name && formState.email && !validationError && (
                <a 
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Save
                </a>
              )}
              
              {/* Only show save button for SMS invite when both name and phone are valid */}
              {member.inviteMethod === 'sms' && formState.name && formState.phone && isValidPhone(formState.phone) && !validationError && (
                <a 
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Save
                </a>
              )}
              
              {/* For other cases (regular members or admin), use existing logic */}
              {!member.inviteMethod && (
                <a
                  className={classNames("btn btn-primary", { 'disabled': !hasValidContactHelper(member, formState) || validationError })}
                  onClick={hasValidContactHelper(member, formState) && !validationError ? handleSave : null}
                >
                  Save
                </a>
              )}
            </>
          )}
          <a 
            className="" 
            onClick={handleCancel}
          >
            Cancel
          </a>
        </div>
      )}
    </div>
  );
};

export default bindAllActions(AlertSetMemberCard);

AlertSetMemberCard.propTypes = {};
AlertSetMemberCard.defaultProps = {};
AlertSetMemberCard.displayName = 'AlertSetMemberCard'
